import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Share2, ChevronRight } from "lucide-react";

export default function Lookbooks() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) {
      api.get("/lookbooks")
        .then(({ data }) => setBooks(data))
        .catch((err) => { console.error(err); setBooks([]); });
    }
  }, [loading, user, navigate]);

  const create = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/lookbooks", { title: title.trim() });
      setTitle("");
      navigate(`/lookbooks/${data.id}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create");
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lookbook?")) return;
    try {
      await api.delete(`/lookbooks/${id}`);
      setBooks((b) => b.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 pt-24 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-8">
            <div className="overline text-zinc-500 mb-3">§ 05 — Look Books</div>
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.9]">Curate a<br /><em className="italic font-light">season.</em></h1>
            <p className="mt-6 max-w-2xl text-lg text-zinc-700 leading-relaxed">
              Group your favourite looks into a single public collection. Send one link. Editors, buyers and press get an editorial spread — no sign-in required.
            </p>
          </div>
          <div className="md:col-span-4 md:pt-8">
            <div className="overline text-zinc-500 mb-2">New book</div>
            <div className="flex gap-1">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="SS27 — Molten"
                className="flex-1 bg-white border border-zinc-300 px-3 py-2 focus:border-lime-500 focus:outline-none font-serif italic text-lg"
                data-testid="lookbook-new-title-input"
                onKeyDown={(e) => e.key === "Enter" && create()}
              />
              <button
                onClick={create}
                disabled={creating || !title.trim()}
                className={`px-4 flex items-center gap-1 overline ${creating || !title.trim() ? "bg-zinc-300 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-950 hover:text-lime-400"} transition-colors`}
                data-testid="lookbook-create-btn"
              >{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</button>
            </div>
          </div>
        </div>

        {books === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>
        ) : books.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center">
            <div className="font-serif italic text-3xl text-zinc-500">No lookbooks yet.</div>
            <p className="text-sm text-zinc-500 mt-2">Type a title above to start your first season.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1" data-testid="lookbooks-grid">
            {books.map((lb) => (
              <div key={lb.id} className="group border-t border-zinc-200 py-6 relative" data-testid={`lookbook-card-${lb.id}`}>
                <button
                  onClick={() => del(lb.id)}
                  className="absolute top-6 right-2 w-8 h-8 opacity-0 group-hover:opacity-100 hover:text-rose-500 transition-all"
                  data-testid={`lookbook-delete-${lb.id}`}
                ><Trash2 className="w-4 h-4" /></button>
                <Link to={`/lookbooks/${lb.id}`} className="block" data-testid={`lookbook-open-${lb.id}`}>
                  <div className="overline text-zinc-500 flex items-center gap-2 mb-2">
                    {lb.design_ids?.length || 0} looks
                    {lb.is_public && <span className="text-lime-600">· Live</span>}
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-4xl leading-tight group-hover:italic transition-all">{lb.title}</h3>
                    <ChevronRight className="w-6 h-6 text-zinc-300 group-hover:text-lime-500 mt-2 transition-colors" />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
